import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';

export default function FactoryFiberPage() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const solutions = [
    {
      title: 'Makine Ağları',
      desc: 'CNC, robot ve otomasyon sistemleri için yüksek hızlı fiber bağlantı',
      icon: 'ri-robot-line',
      features: ['Profinet', 'EtherCAT', 'Modbus TCP', 'OPC UA'],
    },
    {
      title: 'Endüstriyel Ethernet',
      desc: 'Fabrika genelinde yüksek kapasiteli fiber backbone altyapısı',
      icon: 'ri-server-line',
      features: ['Ring Topoloji', 'Redundant Yol', 'Yüksek Kapasite', 'Düşük Gecikme'],
    },
    {
      title: 'Güvenlik Sistemleri',
      desc: 'IP kamera ve güvenlik sistemleri için fiber altyapı',
      icon: 'ri-shield-line',
      features: ['IP Kamera', 'Alarm Sistemi', 'Geçiş Kontrol', 'Merkezi İzleme'],
    },
    {
      title: 'Bina Yönetimi',
      desc: 'SCADA, BMS ve enerji yönetim sistemleri için fiber network',
      icon: 'ri-dashboard-line',
      features: ['SCADA', 'BMS', 'Enerji İzleme', 'Uzaktan Yönetim'],
    },
  ];

  const industries = [
    {
      name: 'Otomotiv Sanayi',
      desc: 'Üretim hattı, robot ağları ve kalite kontrol sistemleri',
      icon: 'ri-car-line',
    },
    {
      name: 'Gıda Üretim',
      desc: 'Hijyenik ortam, otomasyon ve izlenebilirlik sistemleri',
      icon: 'ri-restaurant-line',
    },
    {
      name: 'Tekstil Fabrikaları',
      desc: 'Dokuma makineleri, boya ünitesi ve kalite kontrol ağları',
      icon: 'ri-t-shirt-line',
    },
    {
      name: 'Kimya ve İlaç',
      desc: 'Proses kontrol, güvenlik ve dokümantasyon sistemleri',
      icon: 'ri-flask-line',
    },
    {
      name: 'Lojistik Merkezleri',
      desc: 'Depo yönetimi, otomasyon ve güvenlik altyapısı',
      icon: 'ri-truck-line',
    },
    {
      name: 'Enerji Santralleri',
      desc: 'SCADA, izleme ve kontrol sistemleri için fiber network',
      icon: 'ri-flashlight-line',
    },
  ];

  const faqData = [
    {
      question: 'Endüstriyel fiber kablo ile standart fiber kablo arasındaki fark nedir?',
      answer: 'Endüstriyel fiber kablo, fabrika ortamının zorlu koşullarına dayanıklı özel yapıya sahiptir. Çelik zırh veya aramid koruma ile mekanik darbelere, yağ ve kimyasallara, aşırı sıcaklık değişimlerine dayanıklıdır. EMI/RFI etkilerine karşı tamamen bağışıktır. Standart fiber kablolar ise ofis ve bina içi kullanım için tasarlanmıştır.',
    },
    {
      question: 'Fabrika fiber altyapısı kurulumu ne kadar sürer?',
      answer: 'Fabrika fiber altyapı kurulum süresi, tesisin büyüklüğü ve karmaşıklığına bağlıdır. Orta ölçekli bir fabrika için 2-4 hafta sürmektedir. Büyük ve karmaşık projeler 2-3 ay sürebilir. Proje planlaması, kablo yolu hazırlığı, kablo çekimi, sonlandırma, test ve devreye alma aşamaları bu süreye dahildir.',
    },
    {
      question: 'Mevcut üretim hattına fiber altyapı eklenirken üretim durur mu?',
      answer: 'Hayır, deneyimli ekibimiz üretimi durdurmadan fiber altyapı kurulumu yapabilir. Çalışmalar vardiya dışı saatlerde, hafta sonları veya planlı bakım dönemlerinde gerçekleştirilir. Kritik sistemlerde önce paralel altyapı kurulur, test edilir ve sonra geçiş yapılır. Sıfır üretim kaybı hedeflenir.',
    },
    {
      question: 'Endüstriyel ortamda fiber kablo nasıl korunur?',
      answer: 'Endüstriyel ortamda fiber kablo, çelik boru, galvaniz kanal veya zırhlı kablo kanalı içinde korunur. Yüksek sıcaklık bölgelerinde özel ısıya dayanıklı kanallar kullanılır. Mekanik darbe riski olan bölgelerde çelik zırhlı kablo tercih edilir. Tüm kablo yolları endüstriyel standartlara uygun olarak tasarlanır ve uygulanır.',
    },
    {
      question: 'Fabrika fiber altyapısı için hangi topoloji önerilir?',
      answer: 'Fabrika fiber altyapısında genellikle ring (halka) topoloji önerilir. Ring topoloji, bir noktada kesinti olduğunda trafiğin alternatif yoldan devam etmesini sağlar (redundancy). Kritik uygulamalar için çift ring veya mesh topoloji kullanılabilir. Topoloji seçimi, fabrikanın yapısı, makine yerleşimi ve kritiklik seviyesine göre belirlenir.',
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <title>Fabrika Fiber Optik Çözümleri İstanbul | Endüstriyel Fiber Altyapı</title>
      <meta name="description" content="İstanbul ve çevre illerde endüstriyel tesislerde profesyonel fiber optik altyapı kurulumu. Makine ağları, otomasyon sistemleri, SCADA ve güvenlik sistemleri için yüksek kapasiteli fiber çözümler." />
      <meta name="keywords" content="fabrika fiber, endüstriyel fiber, makine ağları, otomasyon fiber, SCADA fiber, endüstriyel ethernet, fabrika altyapı, İstanbul fabrika fiber, Kocaeli endüstriyel fiber" />
      <link rel="canonical" href="https://fiberek.com/fiber-optik-hizmetleri/fabrika-fiber" />
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="min-h-screen bg-dark-950">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-dark-950 to-fuchsia-900/20"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Fabrika Fiber Optik
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-400">
                    Çözümleri
                  </span>
                </h1>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Endüstriyel tesislerde yüksek kapasiteli fiber altyapı kurulumu. Makine ağları, otomasyon sistemleri, 
                  SCADA ve güvenlik sistemleri için dayanıklı fiber çözümler.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <a
                    href="tel:+905537165561"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-300 font-semibold shadow-lg shadow-purple-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line"></i>
                    Hemen Ara
                  </a>
                  <a
                    href="https://wa.me/905537165561"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    WhatsApp
                  </a>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-purple-400 mb-1">200+</div>
                    <div className="text-sm text-gray-400">Fabrika Projesi</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-fuchsia-400 mb-1">10 Gbps</div>
                    <div className="text-sm text-gray-400">Hız Kapasitesi</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-pink-400 mb-1">99.9%</div>
                    <div className="text-sm text-gray-400">Uptime Garantisi</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative w-full h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/0ad2a40c988a7014d934ac71a0f80257.jpg"
                    alt="Fabrika Fiber Çözümleri"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Solutions */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Endüstriyel Fiber Çözümlerimiz</h2>
              <p className="text-gray-400">Fabrika ve üretim tesisleri için özel fiber optik altyapı hizmetleri</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {solutions.map((solution, index) => (
                <motion.div
                  key={solution.title}
                  ref={ref}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${solution.icon} text-2xl text-purple-400`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{solution.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{solution.desc}</p>
                  <ul className="space-y-1">
                    {solution.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs text-gray-300">
                        <i className="ri-check-line text-purple-400"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-20 bg-gradient-to-br from-purple-900/10 via-dark-950 to-fuchsia-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Hizmet Verdiğimiz Sektörler</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${industry.icon} text-2xl text-purple-400`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{industry.name}</h3>
                  <p className="text-gray-400 text-sm">{industry.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Endüstriyel Fiber Altyapı Özellikleri
                </h2>
                <div className="space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Dayanıklı Kablo Yapısı</h3>
                    <p className="text-gray-400 mb-4">
                      Endüstriyel ortamların zorlu koşullarına dayanıklı özel fiber kablolar kullanılır.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Çelik zırh veya aramid koruma</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Yağ ve kimyasal dayanımı</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>-40°C ile +85°C sıcaklık aralığı</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>UV ve nem koruması</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">EMI/RFI Bağışıklığı</h3>
                    <p className="text-gray-400 mb-4">
                      Fiber optik kablo, elektromanyetik ve radyo frekans girişimlerinden tamamen etkilenmez.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Yüksek voltaj hatlarına yakın kullanım</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Motor ve jeneratör yakınında güvenli</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Kaynak ve plazma kesim ortamında kullanım</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Sinyal bozulması yok</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Yüksek Bant Genişliği</h3>
                    <p className="text-gray-400 mb-4">
                      Gelecekteki kapasite ihtiyaçlarını karşılayacak yüksek bant genişliği.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>1 Gbps - 100 Gbps hız desteği</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Binlerce cihaz bağlantısı</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Düşük gecikme süresi</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-400 mt-1"></i>
                        <span>Gelecek garantisi</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Endüstriyel Uygulamalar
                </h2>
                <div className="prose prose-invert max-w-none">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10 mb-6">
                    <h3 className="text-xl font-bold text-white mb-3">Makine Ağları ve Otomasyon</h3>
                    <p className="text-gray-400 mb-4">
                      CNC makineleri, robotlar, PLC'ler ve otomasyon sistemleri için yüksek hızlı fiber bağlantı. 
                      Profinet, EtherCAT, Modbus TCP gibi endüstriyel protokoller desteklenir.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Gerçek zamanlı veri iletimi</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Deterministik iletişim</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Yüksek güvenilirlik</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10 mb-6">
                    <h3 className="text-xl font-bold text-white mb-3">SCADA ve Kontrol Sistemleri</h3>
                    <p className="text-gray-400 mb-4">
                      Tesis genelinde merkezi izleme ve kontrol için fiber backbone altyapısı. 
                      SCADA, DCS, BMS sistemleri için güvenilir iletişim.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Merkezi izleme ve kontrol</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Alarm ve olay yönetimi</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Veri loglama ve raporlama</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10 mb-6">
                    <h3 className="text-xl font-bold text-white mb-3">Güvenlik ve Kamera Sistemleri</h3>
                    <p className="text-gray-400 mb-4">
                      Yüksek çözünürlüklü IP kameralar, alarm sistemleri ve geçiş kontrol için fiber altyapı. 
                      Merkezi kayıt ve izleme sistemi.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>4K/8K IP kamera desteği</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Merkezi kayıt sistemi</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Uzaktan erişim ve izleme</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Redundant Altyapı</h3>
                    <p className="text-gray-400 mb-4">
                      Kritik uygulamalar için yedekli fiber altyapı. Ring topoloji ile kesintisiz hizmet garantisi.
                    </p>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Ring veya mesh topoloji</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>Otomatik yol değiştirme</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-purple-400 mt-1"></i>
                        <span>99.9% uptime garantisi</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gradient-to-br from-purple-900/10 via-dark-950 to-fuchsia-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                Fabrika Fiber Altyapı Kurulum Süreci
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-purple-400">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Keşif ve Analiz</h3>
                <p className="text-gray-400 text-sm">
                  Fabrika yapısı, makine yerleşimi, mevcut altyapı ve ihtiyaçlar detaylı olarak incelenir. 
                  Teknik rapor ve proje planı hazırlanır.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-fuchsia-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-fuchsia-400">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Tasarım ve Planlama</h3>
                <p className="text-gray-400 text-sm">
                  Network topolojisi, kablo yolları, ekipman seçimi ve montaj planı hazırlanır. 
                  Redundancy ve güvenlik önlemleri belirlenir.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-pink-400">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Kablo Yolu Hazırlığı</h3>
                <p className="text-gray-400 text-sm">
                  Çelik boru, galvaniz kanal veya kablo kanalı montajı yapılır. 
                  Tüm kablo yolları endüstriyel standartlara uygun hazırlanır.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-purple-400">4</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Fiber Kablo Çekimi</h3>
                <p className="text-gray-400 text-sm">
                  Endüstriyel fiber kablolar profesyonel ekipman ile çekilir. 
                  Kablo sabitleme ve etiketleme yapılır.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-fuchsia-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-fuchsia-400">5</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Sonlandırma ve Test</h3>
                <p className="text-gray-400 text-sm">
                  Fiber sonlandırma, patch panel kurulumu ve OTDR testi yapılır. 
                  Tüm hatlar test edilir ve raporlanır.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10"
              >
                <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-pink-400">6</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Devreye Alma</h3>
                <p className="text-gray-400 text-sm">
                  Sistem devreye alınır, kullanıcı eğitimi verilir. 
                  Dokümantasyon ve garanti belgesi teslim edilir.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Sıkça Sorulan Sorular
              </h2>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.details
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-dark-900 rounded-xl border border-white/10 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-2xl text-purple-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </motion.details>
              ))}
            </div>
          </div>
        </section>

        {/* Blog & Rehberler Section */}
        <section className="py-20 bg-gradient-to-br from-purple-900/10 via-dark-950 to-fuchsia-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Blog & Rehberler</h2>
              <p className="text-gray-400">Fiber optik teknolojileri hakkında detaylı bilgiler</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20industrial%20factory%20with%20fiber%20optic%20network%20infrastructure%2C%20automated%20production%20line%20with%20robotic%20arms%20connected%20by%20glowing%20purple%20fiber%20cables%2C%20high-tech%20manufacturing%20facility&width=400&height=250&seq=factory-blog1&orientation=landscape"
                    alt="Fabrikalarda Fiber Altyapı"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-purple-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fabrikalarda Fiber Altyapı Rehberi</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Endüstriyel tesislerde fiber optik altyapı planlama, kurulum ve yönetim süreçleri hakkında kapsamlı rehber.</p>
                  <a
                    href="/blog/fabrikalarda-fiber-altyapi-rehberi"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fusion%20splicing%20fiber%20optic%20process%2C%20close-up%20view%20of%20fiber%20fusion%20splicer%20machine%20in%20operation%2C%20precise%20fiber%20alignment%20and%20welding%2C%20professional%20technical%20environment&width=400&height=250&seq=factory-blog2&orientation=landscape"
                    alt="Fusion Splicing"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-purple-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fusion Splicing Nedir? Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik kabloların kaynaştırılarak birleştirilmesi işlemi olan fusion splicing tekniği ve uygulama detayları.</p>
                  <a
                    href="/blog/fusion-splicing-nedir"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=OTDR%20fiber%20optic%20testing%20equipment%20display%20showing%20measurement%20results%2C%20professional%20technician%20analyzing%20fiber%20test%20data%2C%20modern%20testing%20laboratory%20with%20technical%20instruments&width=400&height=250&seq=factory-blog3&orientation=landscape"
                    alt="OTDR Test"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-purple-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">OTDR Testi Nedir ve Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik hatların kalite kontrolü ve arıza tespiti için kullanılan OTDR test yöntemleri ve uygulama aşamaları.</p>
                  <a
                    href="/blog/otdr-testi-nedir"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-purple-900/20 via-dark-950 to-fuchsia-900/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Fabrika Fiber Altyapı Projesi İçin Teklif Alın
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Ücretsiz keşif ve detaylı proje planlaması
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="tel:+905537165561"
                className="inline-flex items-center gap-2 px-8 py-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-purple-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                +90 553 716 55 61
              </a>
              <a
                href="https://wa.me/905537165561"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingButtons />
      </div>
    </>
  );
}